import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { ThemeServiceService } from '../../utilities/services/theme-service.service';
import { Subject, takeUntil } from 'rxjs';
import { CommonServiceService } from '../../utilities/services/common-service/common-service.service';
import { FooterConfig, SocialLink } from './footer.interface';
import { HttpClient } from '@angular/common/http';
import { TemplateService } from '../../utilities/services/template/template.service';

const DEFAULT_SOCIAL_ICONS: Record<string, string> = {
  Facebook:
    '{{commonStorageUrl}}/UserProfile/c_20260319-113035.png',
  LinkedIn:
    '{{commonStorageUrl}}/UserProfile/Social%20Icons_20260319-113114.png',
  youtube:
    '{{commonStorageUrl}}/UserProfile/Social%20Icons%20%281%29_20260319-113128.png',
  Twitter:
    '{{commonStorageUrl}}/UserProfile/Social%20Icons%20%282%29_20260319-113144.png',
};

function resolveFooterTenantName(tenantName: string | undefined): string {
  if (tenantName === 'switz' || tenantName === 'test_switz') {
    return 'switz';
  }
  return tenantName || 'stupa';
}

@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.scss',
})
export class FooterComponent implements OnInit, OnDestroy {
  environment = environment;
  tenantName = environment.tenant_Name || 'STUPA';
  tenant_name = resolveFooterTenantName(environment.tenant_Name);
  supportPhone = '+41 32 344 07 07';
  supportEmail = 'info@swisstennis-padel.ch';
  websiteUrl = (environment as any).website_url || 'https://stupasports.ai/';
  commonStorageUrl = environment.common_storage_url ?? 'https://storage.googleapis.com/stupaprodsguscentral/';

  // Show footer only for switz/staging
  showFooter: boolean =
    (environment as any)?.show_home_membership_cards ?? false;

  // Logo properties - matching header approach
  logo_light: string = environment.logo_light;
  logo_dark: string = environment.logo_dark;
  is_dark: boolean = false;

  private readonly themeService = inject(ThemeServiceService);
  private readonly templateService = inject(TemplateService);
  private readonly destroy$ = new Subject<void>();
  /** Footer logo; tenants can override it via the optional `footer_logo` env key. */
  get client_logo(): string {
    const env = environment as typeof environment & {
      footer_logo?: string;
    };
    return env.footer_logo ?? env.logo_light;
  }
  brand_logo: string =
    environment.tenant_Name ==='spain'
      ? '../../../assets/logo/brand_light_logo.png'
      : '../../../assets/logo/stupa-new-logo.png';
  brand_logo_light: string = '../../../assets/logo/brand_light_logo.png';

  // Social media links - can be configured in environment
  socialLinks = {
    facebook:
      (environment as any).socialLinks?.facebook ||
      'https://www.facebook.com/people/Swiss-Tennis-Padel/61562550913629/?ref=1',
    instagram:
      (environment as any).socialLinks?.instagram ||
      'https://www.instagram.com/swiss_tennis_padel/',
    tiktok:
      (environment as any).socialLinks?.tiktok ||
      'https://www.tiktok.com/@swiss_tennis_padel?_r=1&_t=ZN-92iJReXInpu',
  };

  // Navigation links
  navigationLinks = {
    aboutUs: 'https://www.swisstennis-padel.ch/de/ueber-uns/',
    termsAndConditions:
      'https://drive.google.com/drive/folders/1mas36Hsy1ECuK4YaDqTCqhrBn-X9suls',
    privacyPolicy:
      'https://drive.google.com/drive/folders/1JlLteoc-qvRRqu5E-jMswXjng0YEobsP',
  };

  currentYear = new Date().getFullYear();
  selectedFooterTemplateId: number = 0;
  /** CMS footer config — API returns a single object, not an array. */
  footerConfig: FooterConfig | undefined = undefined;

  get isTemplate1(): boolean {
    return this.templateService.isTemplate1;
  }

  constructor(
    private readonly commonService: CommonServiceService,
    private readonly http: HttpClient,
  ) {
    this.themeService.currentTheme$.subscribe((theme: string) => {
      this.getBrandLogo(this.setVariant(theme));
    });
  }
  openHome() {
    if (
      this.tenant_name.toLowerCase() === 'switz' ||
      this.tenant_name.toLowerCase() === 'preprod'
    ) {
      globalThis.window.location.href = 'https://www.swisstennis-padel.ch/de/';
    } else {
      // Default behavior - could navigate to home route if needed
    }
  }

  ngOnInit() {
    // Subscribe to theme changes
    this.themeService.isDark$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isDark: boolean) => {
        this.is_dark = isDark;
      });

    // Initialize theme value
    this.is_dark = this.themeService.getTheme();
    this.loadFooterConfig();
    if (this.tenant_name == 'switz' || this.tenant_name == 'test_switz') {
      this.getBrandLogo('darker');
    }
  }

  getBrandLogo(variant: string): void {
    this.http.get('../../../assets/brand_logo.json').subscribe((res: any) => {
      this.brand_logo = res.find(
        (logo: any) => logo.variant == variant,
      )?.src;
    });
  }

  setVariant(theme: string): string {
    if (
      (theme == 'switz-custom-theme' ||
        theme == 'test_switz-custom-theme' ||
        theme == 'darker') &&
      (this.footerConfig?.footer_template_id == 2 ||
        this.footerConfig?.footer_template_id == 4)
    ) {
      return 'darker';

    } else {
      return 'lighter';
    }

    // add condition like this for other tenants theme
    // else if (theme == 'rfetm-custom-theme') {
    //   return 'lighter';
    // }
  }
  selectTemplate(id: number): void {
    this.selectedFooterTemplateId = id;
  }
  loadFooterConfig(): void {
    this.commonService
      .getFooterConfig()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.footerConfig = res.data as FooterConfig;
        },
        error: () => undefined,
        complete: () => undefined,
      });
  }

  getSocialIconUrlFromLink(link: SocialLink): string | null {
    if (link.icon_url) return link.icon_url;
    if (link.is_default) {
      const key = Object.keys(DEFAULT_SOCIAL_ICONS).find(
        (k) => k.toLowerCase() === link.platform?.toLowerCase(),
      );
      return key ? DEFAULT_SOCIAL_ICONS[key] : null;
    }
    return null;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackByIndex(index: number, item: any): number {
    return index;
  }
}
